/**
 * Business Intelligence (rifatta da zero, Davide 27/10/2026).
 *
 * Le sue regole: tutti i dati che hanno senso, ognuno con la sua «i» che dice
 * cosa e' e come e' calcolato coi numeri veri; tutti gli importi SENZA IVA, e
 * scritto; ogni dato incerto segnato (≈ stimato, ? senza margine) col motivo.
 *
 * I numeri arrivano da ``/api/economics/dashboard`` (blocco ``dettagli``):
 * margini materializzati in ``economics.order_margins``, mai ricalcolati qui.
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { prettifyApiError } from "@/shared/api";
import {
  localIsoDate,
  refreshMargins,
  startBackfillAll,
  useBiDashboard,
  type BiDettagli,
  type BiFilterState,
} from "./biApi";
import {
  logoCanale,
  nomeCanale,
  nomeFornitore,
  regolaCommissione,
  SPIEGA_FORNITORE_IGNOTO,
  spiegaFonteFornitore,
} from "./biTesti";
import { SitoMarketplace, SpeseFatture, UtileVeroTestata } from "./components/UtileVero";
import { Affidabilita } from "./components/Affidabilita";
import { Andamento } from "./components/Andamento";
import {
  CostiAllService,
  InPerdita,
  Paesi,
  PostVendita,
  Prodotti,
  Stati,
} from "./components/Dettagli";
import { MotiviClienti, PezziRicambio, ProdottiProblemi } from "./components/Problemi";
import {
  ComponentiRotti,
  CostoPostVendita,
  DaDireAlFornitore,
  DossierFornitori,
  ModelliProblemi,
  UltimiRicambi,
} from "./components/ResiSostituzioni";
import { InfoTip } from "./components/InfoTip";
import { Pillole, Riquadro } from "./components/Riquadro";
import { Schede, vistaDa, type VistaId } from "./components/Schede";
import { TabellaGruppi, type Gruppo } from "./components/TabellaGruppi";
import { Testata } from "./components/Testata";

type PeriodoId = "7" | "30" | "90" | "anno" | "365" | "tutto" | "libero";

const PERIODI: { id: PeriodoId; nome: string }[] = [
  { id: "7", nome: "7 giorni" },
  { id: "30", nome: "30 giorni" },
  { id: "90", nome: "90 giorni" },
  { id: "anno", nome: "Quest'anno" },
  { id: "365", nome: "12 mesi" },
  { id: "tutto", nome: "Tutto" },
  // Davide 03/11/2026: «la possibilita' di selezionare la data che si vuole in modo libero».
  { id: "libero", nome: "Date libere" },
];

function inizioPeriodo(id: PeriodoId): string | null {
  const d = new Date();
  if (id === "tutto" || id === "libero") return null;
  if (id === "anno") return `${d.getFullYear()}-01-01`;
  // «7 giorni» = oggi e i 6 prima: il periodo di confronto ha la stessa durata.
  d.setDate(d.getDate() - (Number(id) - 1));
  return localIsoDate(d);
}

const FORNITORI = [
  "ityhome",
  "itamoby",
  "cosma",
  "allservice",
  "web_furniture",
  "bizzotto",
  "mauro_ferretti",
  "aurora",
  "bscom",
  "unknown",
];

const SELECT =
  "h-[34px] min-w-0 rounded-full border border-line-2 bg-card px-3 text-[13px] font-semibold text-ink transition-[border-color] hover:border-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 max-md:h-11 max-md:flex-1 max-md:text-[15px]";

export function DashboardPage() {
  const [periodo, setPeriodo] = useState<PeriodoId>("30");
  const [dal, setDal] = useState(() => inizioPeriodo("30") ?? "");
  const [al, setAl] = useState(() => localIsoDate(new Date()));
  const [canale, setCanale] = useState("");
  const [fornitore, setFornitore] = useState("");
  const [canaliVisti, setCanaliVisti] = useState<string[]>([]);
  const [messaggio, setMessaggio] = useState<{ testo: string; errore: boolean } | null>(null);
  const [occupato, setOccupato] = useState(false);

  const filtri = useMemo<BiFilterState>(
    () => ({
      dateFrom: periodo === "libero" ? dal || null : inizioPeriodo(periodo),
      dateTo: periodo === "libero" ? al || null : null,
      marketplaceId: canale || null,
      supplierKind: fornitore || null,
    }),
    [periodo, dal, al, canale, fornitore],
  );
  const { data, isLoading, error, refresh } = useBiDashboard(filtri);
  const [cerca, setCerca] = useSearchParams();
  const vista = vistaDa(cerca.get("vista"));
  function scegliVista(v: VistaId) {
    setCerca(
      (prima) => {
        const dopo = new URLSearchParams(prima);
        if (v === "riepilogo") dopo.delete("vista");
        else dopo.set("vista", v);
        return dopo;
      },
      { replace: true },
    );
  }
  const d = data?.dettagli ?? null;

  // Le scelte del filtro canale si accumulano: filtrando un canale la risposta
  // ne contiene uno solo, e la tendina non deve svuotarsi.
  useEffect(() => {
    if (!d) return;
    setCanaliVisti((prima) =>
      [...new Set([...prima, ...d.canali.map((c) => c.marketplace)])].sort(),
    );
  }, [d]);

  const canali: Gruppo[] = (d?.canali ?? []).map((c) => ({
    ...c,
    chiave: c.marketplace,
    nome: nomeCanale(c.marketplace),
    logo: logoCanale(c.marketplace),
    spiegaNome: {
      titolo: nomeCanale(c.marketplace),
      testo: `Commissione: ${regolaCommissione(c.marketplace)}.`,
    },
  }));
  const fornitori: Gruppo[] = (d?.fornitori ?? []).map((f) => ({
    ...f,
    chiave: f.supplier_kind,
    nome: nomeFornitore(f.supplier_kind),
    spiegaNome:
      f.supplier_kind === "unknown"
        ? { titolo: "Fornitore non identificato", testo: SPIEGA_FORNITORE_IGNOTO }
        : spiegaFonteFornitore(
            nomeFornitore(f.supplier_kind),
            f.orders,
            f.from_invoice_count ?? 0,
            f.from_catalog_count ?? 0,
          ),
  }));

  async function ricalcola() {
    setOccupato(true);
    setMessaggio(null);
    try {
      await refreshMargins();
      setMessaggio({
        testo: "Ricalcolo partito: i numeri si aggiornano in pochi minuti.",
        errore: false,
      });
      await refresh();
    } catch (err) {
      setMessaggio({ testo: prettifyApiError(err), errore: true });
    } finally {
      setOccupato(false);
    }
  }

  async function storico() {
    if (
      !window.confirm(
        "Scaricare di nuovo tutto lo storico ordini? Ci vuole tempo, poi premi «Ricalcola margini».",
      )
    )
      return;
    setOccupato(true);
    setMessaggio(null);
    try {
      const r = await startBackfillAll();
      setMessaggio({ testo: `Storico in coda (lavoro n. ${r.job_id}).`, errore: false });
    } catch (err) {
      setMessaggio({ testo: prettifyApiError(err), errore: true });
    } finally {
      setOccupato(false);
    }
  }

  return (
    <main className="bi-pagina min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-paper p-6 scroll-paper max-md:p-3">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-4 max-md:mb-3">
        <div>
          <h1 className="font-display text-[30px] font-bold leading-[1.1] tracking-[-0.02em] max-md:sr-only">
            Business Intelligence
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-[13px] max-md:text-[14px]">
            Vendite e margini di tutti i canali.
            <span className="inline-flex h-[24px] items-center gap-1.5 rounded-full bg-ink px-2.5 text-[12px] font-bold text-on-ink">
              Tutti gli importi senza IVA
            </span>
            <InfoTip
              spiegazione={{
                titolo: "Senza IVA",
                testo:
                  "Ogni euro della pagina e' senza IVA. Il fatturato e' il prezzo pagato meno l'IVA del paese (per Kery quella vera di Shopify). Costi di prodotto, commissioni, spedizione e AllService sono imponibili. Le sostituzioni AllService scritte a mano con IVA sono divise per 1,22.",
              }}
            />
          </p>
          {d?.archivio && <ArchivioFino a={d.archivio} />}
        </div>
        <div className="flex flex-wrap items-center gap-2 max-md:w-full">
          <div className="max-md:w-full max-md:overflow-x-auto">
            <Pillole etichetta="Periodo" valore={periodo} onChange={setPeriodo} opzioni={PERIODI} />
          </div>
          {periodo === "libero" && (
            <>
              <label className="flex items-center gap-1.5 text-[13px] font-semibold max-md:flex-1">
                Dal
                <input
                  type="date"
                  aria-label="Dal giorno"
                  value={dal}
                  max={al || undefined}
                  onChange={(e) => setDal(e.target.value)}
                  className={SELECT}
                />
              </label>
              <label className="flex items-center gap-1.5 text-[13px] font-semibold max-md:flex-1">
                al
                <input
                  type="date"
                  aria-label="Al giorno"
                  value={al}
                  min={dal || undefined}
                  onChange={(e) => setAl(e.target.value)}
                  className={SELECT}
                />
              </label>
            </>
          )}
          <select
            aria-label="Canale"
            value={canale}
            onChange={(e) => setCanale(e.target.value)}
            className={SELECT}
          >
            <option value="">Tutti i canali</option>
            {canaliVisti.map((c) => (
              <option key={c} value={c}>
                {nomeCanale(c)}
              </option>
            ))}
          </select>
          <select
            aria-label="Fornitore"
            value={fornitore}
            onChange={(e) => setFornitore(e.target.value)}
            className={SELECT}
          >
            <option value="">Tutti i fornitori</option>
            {FORNITORI.map((f) => (
              <option key={f} value={f}>
                {nomeFornitore(f)}
              </option>
            ))}
          </select>
        </div>
      </header>

      <ErrorBanner message={error} className="mb-4" />
      {data && !d && (
        <ErrorBanner
          message="Il server non manda ancora i dati della nuova BI: serve il rilascio del backend."
          className="mb-4"
        />
      )}
      {isLoading && !d && (
        <p className="py-20 text-center text-[14px] font-semibold">Carico i numeri…</p>
      )}

      {d && (
        <div
          className={`transition-opacity duration-300 ${isLoading ? "opacity-60" : ""}`}
          aria-busy={isLoading}
        >
          <Schede valore={vista} onChange={scegliVista} />

          {vista === "riepilogo" && <Testata d={d} />}
          {vista === "riepilogo" && (
            <div className="grid grid-flow-dense grid-cols-3 gap-4 max-xl:grid-cols-1">
              <div className="col-span-2 max-xl:col-span-1">
                <Andamento
                  giorni={d.giorni}
                  key={`${periodo}-${dal}-${al}-${canale}-${fornitore}`}
                />
              </div>
              <Affidabilita e={d.economia} motivi={d.motivi} />
              <div className="col-span-3 max-xl:col-span-1">
                <PostVendita d={d} />
              </div>
            </div>
          )}

          {vista === "canali" && (
            <div className="flex flex-col gap-4">
              <Riquadro
                titolo="Canali"
                spiegazione={{
                  titolo: "Canali",
                  testo:
                    "Ogni canale di vendita, dal piu' grande. Costi e margine solo sugli ordini calcolabili; la colonna Affidabilita' dice quanti sono stimati o senza margine. Temu non trattiene commissione: 0%.",
                }}
              >
                <TabellaGruppi gruppi={canali} etichetta="Canale" />
              </Riquadro>
              <Riquadro
                titolo="Fornitori"
                spiegazione={{
                  titolo: "Fornitori",
                  testo:
                    "Chi ha venduto la merce. OM lo prende dal codice dell'ordine fornitore, poi dalla fattura d'acquisto, poi dal catalogo (PIM) se tutte le righe sono dello stesso fornitore. La «i» accanto al nome dice quanti da ogni fonte. Mai indovinato.",
                }}
              >
                <TabellaGruppi gruppi={fornitori} etichetta="Fornitore" />
              </Riquadro>
              <Paesi righe={d.paesi} />
            </div>
          )}

          {vista === "prodotti" && (
            <div className="grid grid-flow-dense grid-cols-3 gap-4 max-xl:grid-cols-1">
              <div className="col-span-2 max-xl:col-span-1">
                <Prodotti p={d.prodotti} />
              </div>
              <InPerdita righe={d.in_perdita} />
            </div>
          )}

          {vista === "utile" &&
            (d.costi_struttura ? (
              <>
                <UtileVeroTestata c={d.costi_struttura} />
                <div className="grid grid-flow-dense grid-cols-3 gap-4 max-xl:grid-cols-1">
                  <div className="col-span-2 max-xl:col-span-1">
                    <SitoMarketplace c={d.costi_struttura} />
                  </div>
                  <SpeseFatture c={d.costi_struttura} />
                </div>
              </>
            ) : (
              <p className="py-10 text-center text-[14px] font-semibold">
                {d.costi_struttura === null
                  ? "L'utile vero vale per tutta l'azienda: togli il filtro di canale o di fornitore per vederlo."
                  : "Il server non manda ancora l'utile vero: serve il rilascio del backend."}
              </p>
            ))}

          {vista === "resi" &&
            (d.costi_post_vendita && d.dossier_trend ? (
              <>
                <CostoPostVendita c={d.costi_post_vendita} d={d} />
                <div className="grid grid-flow-dense grid-cols-3 gap-4 max-xl:grid-cols-1">
                  <div className="col-span-2 max-xl:col-span-1">
                    <DossierFornitori t={d.dossier_trend} />
                  </div>
                  <div className="flex min-w-0 flex-col gap-4">
                    <DaDireAlFornitore t={d.dossier_trend} />
                    <UltimiRicambi t={d.dossier_trend} quanti={5} />
                  </div>
                  <ComponentiRotti t={d.dossier_trend} />
                  <div className="col-span-2 max-xl:col-span-1">
                    <ModelliProblemi t={d.dossier_trend} />
                  </div>
                </div>
              </>
            ) : (
              <p className="py-10 text-center text-[14px] font-semibold">
                Il server non manda ancora resi e sostituzioni: serve il rilascio del backend.
              </p>
            ))}

          {vista === "problemi" &&
            (d.problemi ? (
              <div className="grid grid-flow-dense grid-cols-3 gap-4 max-xl:grid-cols-1">
                <div className="col-span-2 max-xl:col-span-1">
                  <ProdottiProblemi p={d.problemi} />
                </div>
                <div className="flex min-w-0 flex-col gap-4">
                  <MotiviClienti p={d.problemi} />
                  <PezziRicambio p={d.problemi} />
                </div>
              </div>
            ) : (
              <p className="py-10 text-center text-[14px] font-semibold">
                Il server non manda ancora i problemi: serve il rilascio del backend.
              </p>
            ))}

          {vista === "costi" && (
            <div className="grid grid-flow-dense grid-cols-2 gap-4 max-xl:grid-cols-1">
              <CostiAllService />
              <Stati
                righe={data?.by_status ?? []}
                esclusi={data?.filters.excluded_statuses ?? []}
              />
            </div>
          )}

          <footer className="mt-6 flex flex-wrap items-center gap-2 border-t border-line pt-4 text-[13px]">
            <span className="font-bold">Dati</span>
            <InfoTip
              spiegazione={{
                titolo: "Da dove vengono i numeri",
                testo:
                  "OM calcola il margine di ogni ordine ogni 6 ore e lo salva. Un margine chiuso (ordine spedito, conti completi) non cambia piu' se cambiano i listini. «Ricalcola margini» rifa' subito quelli aperti.",
              }}
            />
            <button
              type="button"
              disabled={occupato}
              onClick={() => void ricalcola()}
              className="h-[32px] rounded-full border border-line-2 bg-card px-3.5 font-semibold transition-[border-color,transform] hover:border-ink active:scale-[0.97] disabled:opacity-50"
            >
              Ricalcola margini
            </button>
            <button
              type="button"
              disabled={occupato}
              onClick={() => void storico()}
              className="h-[32px] rounded-full border border-line-2 bg-card px-3.5 font-semibold transition-[border-color,transform] hover:border-ink active:scale-[0.97] disabled:opacity-50"
            >
              Riscarica lo storico
            </button>
            {messaggio && (
              <span role="status" className={`font-semibold ${messaggio.errore ? "text-red" : ""}`}>
                {messaggio.testo}
              </span>
            )}
          </footer>
        </div>
      )}
    </main>
  );
}

function dataPiena(iso: string | null): string {
  if (!iso) return "niente caricato";
  const [y = "", m = "", g = ""] = iso.split("-");
  return `${g}/${m}/${y}`;
}

/** Fin dove arrivano le fatture e l'estratto conto (Davide 03/11/2026): il
 *  prossimo scaricamento parte dal giorno dopo. */
function ArchivioFino({ a }: { a: NonNullable<BiDettagli["archivio"]> }) {
  return (
    <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] max-md:text-[13px]">
      <span>
        Ultima fattura di acquisto: <b className="tabular-nums">{dataPiena(a.acquisto)}</b>
      </span>
      <span>
        Ultima fattura di vendita: <b className="tabular-nums">{dataPiena(a.vendita)}</b>
      </span>
      <span>
        Estratto conto fino al: <b className="tabular-nums">{dataPiena(a.banca)}</b>
      </span>
      <InfoTip
        spiegazione={{
          titolo: "Da quando scaricare",
          testo:
            "Le date dell'ultima fattura d'acquisto e di vendita arrivate in OM, e l'ultimo giorno dell'estratto conto caricato in Saldi. Il prossimo scaricamento da Eureka o dalla banca parte da qui. Le vendite sono quelle passate dallo SdI: gli scontrini (SF) non ci sono.",
        }}
      />
    </p>
  );
}
